import { runInAction } from "mobx";
import type { FetchByQueryResult, Segment } from "mobx-repository";
import { prop } from "ramda";
import type {
    PageInfo,
    UserGroupAssignmentCreate,
    UserGroupAssignmentsApi,
    UserGroupAssignmentsListUserGroupAssignmentsRequest,
    UserGroupAssignmentSortKey,
    UserGroupAssignmentUpdate,
    UserGroupAssignment,
} from "../../api";
import type { ApiResource } from "../../utils/elofleet-repository";
import { ElofleetRepository } from "../../utils/elofleet-repository";
import type { UUID } from "../../utils/uuid";
import { Validation } from "../../utils/validation";
import { defaultPageSize } from "../../utils/constants";

export type UserGroupAssignmentsQuery = Omit<
    UserGroupAssignmentsListUserGroupAssignmentsRequest,
    "page"
>;

export interface UserGroupAssignmentsApiResource extends ApiResource {
    entity: UserGroupAssignment;
    query: UserGroupAssignmentsQuery;
    update: UserGroupAssignmentUpdate;
    create: UserGroupAssignmentCreate;
    sortKey: UserGroupAssignmentSortKey;
}

export class RepositoryUserGroupAssignments extends ElofleetRepository<UserGroupAssignmentsApiResource> {
    constructor(private readonly userGroupAssignmentsApi: UserGroupAssignmentsApi) {
        super();
    }

    public validation = {
        create: new Validation((userGroupAssignmentCreate: UserGroupAssignmentCreate) =>
            this.userGroupAssignmentsApi.userGroupAssignmentsValidateCreateUserGroupAssignment({
                userGroupAssignmentCreate: userGroupAssignmentCreate,
            }),
        ),
        update: new Validation(
            (userGroupAssignmentUpdate: UserGroupAssignmentUpdate, userGroupAssignmentId: UUID) =>
                this.userGroupAssignmentsApi.userGroupAssignmentsValidateUpdateUserGroupAssignment({
                    userGroupAssignmentUpdate,
                    userGroupAssignmentId,
                }),
        ),
    };

    protected async fetchByQuery(
        query: UserGroupAssignmentsQuery,
        pagination: Segment,
    ): Promise<FetchByQueryResult<UserGroupAssignment>> {
        const page = pagination.offset / this.defaultCount;
        const entities =
            await this.userGroupAssignmentsApi.userGroupAssignmentsListUserGroupAssignments({
                ...query,
                page,
            });
        return { entities };
    }

    public fetchMetadata(query: UserGroupAssignmentsQuery): Promise<PageInfo> {
        return this.userGroupAssignmentsApi.userGroupAssignmentsListUserGroupAssignmentsMetadata(
            query,
        );
    }

    protected async fetchById(
        userGroupAssignmentId: UUID,
    ): Promise<UserGroupAssignment | undefined> {
        return await this.userGroupAssignmentsApi.userGroupAssignmentsReadUserGroupAssignment({
            userGroupAssignmentId,
        });
    }

    protected extractId = prop("id");

    public async update(
        userGroupAssignmentId: UUID,
        userGroupAssignmentUpdate: UserGroupAssignmentUpdate,
    ): Promise<UserGroupAssignment> {
        const userGroupAssignment =
            await this.userGroupAssignmentsApi.userGroupAssignmentsUpdateUserGroupAssignment({
                userGroupAssignmentId,
                userGroupAssignmentUpdate,
            });
        await this.waitForIdle();
        runInAction(() => {
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(userGroupAssignment);
        });
        return userGroupAssignment;
    }

    public async create(
        userGroupAssignmentCreate: UserGroupAssignmentCreate,
    ): Promise<UserGroupAssignment> {
        const userGroupAssignment =
            await this.userGroupAssignmentsApi.userGroupAssignmentsCreateUserGroupAssignment({
                userGroupAssignmentCreate,
            });
        await this.waitForIdle();
        runInAction(() => {
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(userGroupAssignment);
        });
        return userGroupAssignment;
    }

    public async delete(...userGroupAssignmentIds: UUID[]): Promise<void> {
        await Promise.all(
            userGroupAssignmentIds.map((userGroupAssignmentId) =>
                this.userGroupAssignmentsApi.userGroupAssignmentsDeleteUserGroupAssignment({
                    userGroupAssignmentId,
                }),
            ),
        );
        await this.waitForIdle();
        this.reloadQuery({ pageSize: defaultPageSize });
    }
}
